import React from 'react';
import { FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';

/**
 * Props for the FormInput component
 */
interface FormInputProps {
  /** TanStack Form field object containing state and handlers */
  field: any;
  /** Label text displayed above the input */
  label: string;
  /** Placeholder text for the input */
  placeholder?: string;
  /** HTML input type (text, email, password, etc.) */
  type?: string;
  /** Optional custom onChange handler that overrides the default field handler */
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  /** Optional custom onBlur handler called after the default field handler */
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
}

/**
 * A reusable form input component that integrates with TanStack Form and shadcn/ui.
 *
 * This component provides a standardized way to create form inputs with consistent styling
 * and automatic error handling. It's designed to work seamlessly with TanStack Form's
 * field objects while maintaining compatibility with shadcn/ui form components.
 *
 * **Features:**
 * - Automatic integration with TanStack Form field state and handlers
 * - Consistent styling using shadcn/ui components
 * - Automatic error message display via FormMessage
 * - Support for custom onChange handlers when needed
 * - Proper accessibility attributes and form field identification
 *
 * **Design Decisions:**
 * - Accepts a TanStack Form field object to eliminate boilerplate
 * - Uses shadcn/ui components for consistent styling across the application
 * - Provides sensible defaults while allowing customization where needed
 * - Maintains compatibility with HTML input types for flexibility
 *
 * **When to Use:**
 * - For simple text inputs in TanStack Form-based forms
 * - When you want consistent styling without repeating FormItem/FormLabel/Input/FormMessage patterns
 * - For inputs that don't require complex custom behavior (use custom components for those)
 *
 * **When NOT to Use:**
 * - For inputs with complex behavior like autocomplete (use specialized components)
 * - For inputs that need extensive customization beyond basic props
 * - For non-text inputs like selects, checkboxes, etc. (use appropriate specialized components)
 *
 * **Usage Examples:**
 *
 * Basic usage:
 * ```tsx
 * <form.Field
 *   name="email"
 *   children={(field) => (
 *     <FormInput
 *       field={field}
 *       label="Email Address"
 *       type="email"
 *       placeholder="Enter your email"
 *     />
 *   )}
 * />
 * ```
 *
 * With custom onChange handler:
 * ```tsx
 * <form.Field
 *   name="vin"
 *   children={(field) => (
 *     <FormInput
 *       field={field}
 *       label="VIN"
 *       placeholder="Enter VIN"
 *       onChange={(e) => {
 *         field.handleChange(e.target.value);
 *         // Custom logic, like clearing related fields
 *         clearSuggestions();
 *       }}
 *       onBlur={(e) => {
 *         // Custom logic after field blur, like loading suggestions
 *         loadSuggestions(e.target.value);
 *       }}
 *     />
 *   )}
 * />
 * ```
 *
 * @param props - Configuration object for the form input
 * @returns JSX element containing the complete form input with label and error handling
 */
export function FormInput({
  field,
  label,
  placeholder,
  type = 'text',
  onChange,
  onBlur,
}: FormInputProps) {
  return (
    <FormItem>
      <FormLabel>{label}</FormLabel>
      <Input
        id={field.name}
        name={field.name}
        type={type}
        placeholder={placeholder}
        value={field.state.value ?? ''}
        onBlur={(e: React.FocusEvent<HTMLInputElement>) => {
          field.handleBlur(e);
          onBlur?.(e);
        }}
        onChange={
          onChange ||
          ((e: React.ChangeEvent<HTMLInputElement>) =>
            field.handleChange(e.target.value))
        }
        data-form-field={true}
      />
      {/* Automatically displays validation errors from TanStack Form */}
      <FormMessage />
    </FormItem>
  );
}
